import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["name", "options", "error", "fullWidth", "label", "deviceSize", "theme", "searchListField", "noFoundText", "onItemClick", "multiple", "value", "onChange", "disabled", "filterName"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, MenuItem, SwipeableDrawer, Typography } from '@mui/material';
import { longTextReplacer } from '@utils';
import { Button } from '@elements/button';
import { CheckboxField } from '@elements/form';
import { CloseIcon, SelectIcon } from '@elements/form/fields/select-field/select-field.helpers';
import { Tooltip } from '@elements/tooltip';
import { pxToRem } from '@themes';
import { DrawerContainer, ItemsContainer, Puller, ScrollHandler, SelectFooter, SelectHeader, SelectMenuStyles, SelectPaperStyles, StyledButton, StyledFormControl, StyledFormHelperErrorText, StyledInputLabel, StyledSelectBase, StyledTextField, StyleFromHelperWrapper } from './list-field.styles';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

/**
 * List field component.
 *
 * @author Mikhail Siutsou
 * @function ListField
 * @category components
 * @param {string} name field name.
 * @param {SelectItemProps[]} items field items.
 * @param {string | boolean} error field error.
 * @param {boolean} fullWidth field width.
 * @param {string} label field label.
 * @param {string} color field color.
 * @param {DeviceSize} deviceSize device size.
 * @param {DefaultTheme} theme theme.
 * @param {Partial<ListFieldProps>} restProps field rest props.
 * @return {FC} list field component.
 */
export var ListField = function ListField(_ref) {
  var _options$filter, _options$filter2;

  var name = _ref.name,
      options = _ref.options,
      error = _ref.error,
      fullWidth = _ref.fullWidth,
      label = _ref.label,
      deviceSize = _ref.deviceSize,
      theme = _ref.theme,
      searchListField = _ref.searchListField,
      noFoundText = _ref.noFoundText,
      onItemClick = _ref.onItemClick,
      multiple = _ref.multiple,
      value = _ref.value,
      onChange = _ref.onChange,
      disabled = _ref.disabled,
      _ref$filterName = _ref.filterName,
      filterName = _ref$filterName === void 0 ? '' : _ref$filterName,
      restProps = _objectWithoutProperties(_ref, _excluded);

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      open = _useState2[0],
      setOpen = _useState2[1];

  var _useState3 = useState(value || []),
      _useState4 = _slicedToArray(_useState3, 2),
      values = _useState4[0],
      setValues = _useState4[1];

  var _useState5 = useState(value || []),
      _useState6 = _slicedToArray(_useState5, 2),
      labels = _useState6[0],
      setLabels = _useState6[1];

  var _useState7 = useState(value || []),
      _useState8 = _slicedToArray(_useState7, 2),
      appliedLabels = _useState8[0],
      setAppliedLabels = _useState8[1];

  var _useState9 = useState(''),
      _useState10 = _slicedToArray(_useState9, 2),
      inputValue = _useState10[0],
      setInputValue = _useState10[1];

  var _useState11 = useState([]),
      _useState12 = _slicedToArray(_useState11, 2),
      appliedValues = _useState12[0],
      setAppliedValues = _useState12[1];

  var anchorRef = useRef(null);
  var prevOpen = useRef(open);
  var isError = !!error;
  var inputName = name && "".concat(name, "-listfield");
  var literalProps = {
    htmlFor: inputName,
    id: inputName,
    errorId: inputName && "".concat(inputName, "-error-text")
  };
  useEffect(function () {
    if (prevOpen.current && open) {
      var _anchorRef$current;

      (_anchorRef$current = anchorRef.current) === null || _anchorRef$current === void 0 ? void 0 : _anchorRef$current.focus();
    }

    prevOpen.current = open;
  }, [open]);
  useEffect(function () {
    if (value.length === 0 || // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    value.includes(undefined)) {
      setValues([]);
      setAppliedValues([]);
      setAppliedLabels([]);
      setLabels([]);
    }
  }, [value]);
  useEffect(function () {
    if (prevOpen.current && open) {
      var _anchorRef$current2;

      (_anchorRef$current2 = anchorRef.current) === null || _anchorRef$current2 === void 0 ? void 0 : _anchorRef$current2.focus();
    }

    prevOpen.current = open;
  }, [open]);
  /**
   * Toggle handler.
   *
   * @author Ihar Kazlouski
   * @return {void}
   */

  var handleToggle = function handleToggle() {
    setOpen(function (prevOpen) {
      return !prevOpen;
    });
  };
  /**
   * Search text field change handler.
   *
   * @author Ihar Kazlouski
   * @param {event: ChangeEvent<HTMLInputElement>} event event.
   * @return {void}
   */


  var handleInputChange = function handleInputChange(event) {
    if (event && event.target && event.target.value) {
      setInputValue(event.target.value);
    } else {
      setInputValue('');
    }
  };
  /**
   * Close handler.
   *
   * @author Ihar Kazlouski
   * @param {Event | React.SyntheticEvent} event event.
   * @return {void}
   */


  var handleClose = function handleClose(event) {
    if (event && anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };
  /**
   * Drawer item click handler.
   *
   * @author Ihar Kazlouski
   * @param {string} value value.
   * @return {(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void}
   */


  var drawerItemClickHandler = function drawerItemClickHandler(value) {
    return function (event) {
      event.target = {
        value: value,
        name: name
      }; // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore

      if (restProps && restProps.onChange) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        restProps.onChange(event);
      }

      handleClose({});
    };
  };

  var itemsJSX = options === null || options === void 0 ? void 0 : (_options$filter = options.filter(function (option) {
    return option.label.toLowerCase().includes(inputValue.toLowerCase());
  })) === null || _options$filter === void 0 ? void 0 : _options$filter.map(function (_ref2) {
    var label = _ref2.label,
        value = _ref2.value,
        defaultItem = _ref2["default"];
    return /*#__PURE__*/_jsx(StyledButton, {
      fullWidth: true // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      ,
      active: restProps.value === value,
      onClick: drawerItemClickHandler(value),
      $isDefault: defaultItem,
      children: /*#__PURE__*/_jsx(Typography, {
        variant: "body1",
        width: "100%",
        color: "tuna",
        align: "left",
        children: label
      })
    }, value);
  });
  /**
   * Handle change.
   *
   * @author Ihar Kazlouski
   * @param {SelectChangeEvent<unknown>} event event.
   * @return {void}
   */

  var handleClick = function handleClick(_ref3) {
    var label = _ref3.label,
        value = _ref3.value;
    return function () {
      if (values.includes(value)) {
        setValues(function (prevState) {
          return prevState.filter(function (prevItem) {
            return prevItem !== value;
          });
        });
        setLabels(function (prevState) {
          return prevState.filter(function (prevItem) {
            return prevItem !== label;
          });
        });
        return;
      }

      setValues(function (prevState) {
        return [].concat(_toConsumableArray(prevState), [value]);
      });
      setLabels(function (prevState) {
        return [].concat(_toConsumableArray(prevState), [label]);
      });
    };
  };
  /**
   * Mouse down handler.
   *
   * @author Ihar Kazlouski
   * @param {Event | React.SyntheticEvent} event event.
   * @return {void}
   */


  var mouseDownHandler = function mouseDownHandler(event) {
    event.stopPropagation();
  };
  /**
   * Handle delete values.
   *
   * @author Ihar Kazlouski
   * @return {void}
   */


  var handleDelete = function handleDelete() {
    setAppliedValues([]);
    setValues([]);
    setAppliedLabels([]);
    setLabels([]);

    if (onChange) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      onChange([]);
    }
  };
  /**
   * Apply select changes.
   *
   * @author Ihar Kazlouski
   * @return {void}
   */


  var handleApply = function handleApply() {
    setAppliedValues(values);
    setAppliedLabels(labels);
    setOpen(false);

    if (onChange) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      onChange(values);
    }
  };

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(StyledFormControl, {
      error: isError,
      fullWidth: fullWidth,
      children: [label && /*#__PURE__*/_jsx(StyledInputLabel, {
        shrink: true,
        htmlFor: literalProps.htmlFor,
        children: label
      }), /*#__PURE__*/_jsx(Box, {
        children: /*#__PURE__*/_jsxs(StyledSelectBase, _objectSpread(_objectSpread({
          id: literalProps.id,
          "aria-describedby": inputName,
          name: name,
          fullWidth: fullWidth,
          open: open,
          isApplied: !!appliedValues.length,
          onClose: handleClose,
          options: options,
          onOpen: handleToggle,
          IconComponent: !appliedValues.length ? SelectIcon : function () {
            return /*#__PURE__*/_jsx(CloseIcon, {
              handleClick: handleDelete,
              mouseDown: mouseDownHandler
            });
          },
          multiple: multiple,
          value: values,
          disabled: disabled,
          displayEmpty: true,
          renderValue: function renderValue(selected) {
            return appliedLabels.length > 0 ? /*#__PURE__*/_jsxs(Box, {
              whiteSpace: "normal",
              display: "flex",
              children: [/*#__PURE__*/_jsxs(Typography, {
                variant: "body2",
                fontWeight: "500",
                children: [filterName, ":"]
              }), /*#__PURE__*/_jsx(Typography, {
                variant: "body2",
                fontWeight: "500",
                ml: 1.25,
                children: !(appliedLabels.join(', ').length > 15) ? appliedLabels.join(', ') : (appliedLabels === null || appliedLabels === void 0 ? void 0 : appliedLabels.length.toString()) + ' ' + t('common.select.selected')
              })]
            }) : /*#__PURE__*/_jsx(Typography, {
              variant: "body2",
              fontWeight: "500",
              color: "comet",
              children: filterName
            });
          },
          MenuProps: {
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right'
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right'
            },
            sx: {
              display: 'block'
            },
            PaperProps: {
              style: SelectPaperStyles((deviceSize === null || deviceSize === void 0 ? void 0 : deviceSize.mobile) || false, theme, multiple)
            },
            MenuListProps: {
              sx: SelectMenuStyles(theme)
            }
          }
        }, restProps), {}, {
          children: [multiple && /*#__PURE__*/_jsx(SelectHeader, {
            p: 2.5,
            children: /*#__PURE__*/_jsx(StyledTextField, {
              name: "search",
              size: "small",
              placeholder: "Search",
              onChange: handleInputChange,
              value: inputValue,
              type: "search",
              clearable: true
            })
          }), /*#__PURE__*/_jsx(ScrollHandler, {
            multiple: multiple,
            children: options === null || options === void 0 ? void 0 : (_options$filter2 = options.filter(function (option) {
              return option.label.toLowerCase().includes(inputValue.toLowerCase());
            })) === null || _options$filter2 === void 0 ? void 0 : _options$filter2.map(function (option) {
              var _option$label;

              return /*#__PURE__*/_jsxs(MenuItem, {
                value: option.value,
                disabled: option.disabled,
                hidden: option.hidden,
                onClick: handleClick(option),
                children: [multiple && typeof values !== 'string' && /*#__PURE__*/_jsx(CheckboxField, {
                  checked: values.includes(option.value),
                  iconColor: "frenchGray"
                }), /*#__PURE__*/_jsx(Tooltip, {
                  title: (option === null || option === void 0 ? void 0 : (_option$label = option.label) === null || _option$label === void 0 ? void 0 : _option$label.length) > 18 ? option === null || option === void 0 ? void 0 : option.label : '',
                  placement: "bottom-start",
                  children: /*#__PURE__*/_jsx(Typography, {
                    variant: "body2",
                    color: "comet",
                    lineHeight: 2.5,
                    children: longTextReplacer(option.label, 18)
                  })
                })]
              }, option.value);
            })
          }), multiple && /*#__PURE__*/_jsx(SelectFooter, {
            px: 1.5,
            py: 1,
            children: /*#__PURE__*/_jsx(Button, {
              onClick: handleApply,
              variant: "contained",
              fullWidth: true,
              size: "small",
              children: t('common.select.applyFilter')
            })
          })]
        }))
      }), isError && /*#__PURE__*/_jsx(StyleFromHelperWrapper, {
        children: /*#__PURE__*/_jsx(StyledFormHelperErrorText, {
          id: literalProps.errorId,
          children: error
        })
      })]
    }), /*#__PURE__*/_jsx(SwipeableDrawer, {
      anchor: "bottom",
      open: (deviceSize === null || deviceSize === void 0 ? void 0 : deviceSize.mobile) && open || false,
      onClose: handleClose,
      onOpen: handleToggle,
      ModalProps: {
        BackdropProps: {
          invisible: true
        }
      },
      PaperProps: {
        sx: {
          borderTopLeftRadius: pxToRem(24),
          borderTopRightRadius: pxToRem(24)
        }
      },
      children: /*#__PURE__*/_jsxs(DrawerContainer, {
        children: [/*#__PURE__*/_jsx(Puller, {}), label && /*#__PURE__*/_jsx(Box, {
          pb: 4,
          children: /*#__PURE__*/_jsx(Typography, {
            variant: "h3",
            color: "tuna",
            children: label
          })
        }), /*#__PURE__*/_jsx(ItemsContainer, {
          children: itemsJSX
        })]
      })
    })]
  });
};